import { makeStyles, alpha } from "@material-ui/core/styles";
import { AEROS, stateText } from "../../styles/aerosTokens";

// Estilos compartilhados das três abas do Flowbuilder (/flowbuilders,
// /phrase-lists, /flow-default) — header 60px com abas + busca + CTA,
// corpo com padding 20/12/24, tabela em grid, pills e botões de ação
// 30px (mockup "Frases A" / "Fluxo Padrão A").
const useFlowPageStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  return {
    pageHeader: {
      minHeight: 60,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      gap: 12,
      // MainContainer já aplica 16px laterais; 12px aqui fecha os 28px do mockup.
      padding: "0 12px",
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.06)"}`,
      flexWrap: "wrap",
    },
    headerActions: { marginLeft: "auto", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
    searchPill: {
      width: 240,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
      borderRadius: 10,
      padding: "5px 12px",
      fontSize: 13,
      color: theme.palette.text.primary,
      display: "flex",
      alignItems: "center",
      gap: 8,
      "& .MuiSvgIcon-root": { fontSize: 17, color: theme.palette.text.secondary },
      "& input::placeholder": { color: theme.palette.text.secondary, opacity: 1 },
      [theme.breakpoints.down("sm")]: { width: "100%" },
    },
    gradientButton: {
      borderRadius: 10,
      padding: "8px 16px",
      fontSize: 13,
      fontWeight: 700,
      whiteSpace: "nowrap",
      "& .MuiSvgIcon-root": { fontSize: 17 },
    },
    pageBody: {
      flex: 1,
      padding: "20px 12px 24px",
      display: "flex",
      flexDirection: "column",
      gap: 14,
      overflowY: "auto",
      ...theme.scrollbarStyles,
      [theme.breakpoints.down("sm")]: { padding: "16px 4px" },
    },
    infoBox: {
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
      padding: "12px 16px",
      background: "rgba(34,211,238,0.06)",
      border: "1px solid rgba(34,211,238,0.2)",
      borderRadius: 12,
      fontSize: 12.5,
      color: theme.palette.text.secondary,
      lineHeight: 1.5,
      "& b": { color: theme.palette.text.primary },
      "& > .MuiSvgIcon-root": { fontSize: 18, color: stateText(theme, AEROS.cyan, "info"), flex: "none" },
    },
    table: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 14,
      overflow: "hidden",
    },
    tableScroll: { overflowX: "auto", ...theme.scrollbarStyles },
    row: {
      display: "grid",
      alignItems: "center",
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"}`,
      fontSize: 13,
      minWidth: 900,
      "&:last-child": { borderBottom: "none" },
      "&:hover": { background: alpha(theme.palette.primary.main, 0.04) },
    },
    head: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      background: isLight ? "rgba(15,20,40,0.02)" : "rgba(255,255,255,0.02)",
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      "&:hover": { background: isLight ? "rgba(15,20,40,0.02)" : "rgba(255,255,255,0.02)" },
    },
    cell: { padding: "12px 8px", minWidth: 0 },
    cellFirst: { paddingLeft: 20 },
    cellLast: { paddingRight: 20, display: "flex", justifyContent: "flex-end", gap: 6 },
    nameCell: { display: "flex", alignItems: "center", gap: 10 },
    tile: {
      width: 32,
      height: 32,
      borderRadius: 9,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      background: "rgba(160,68,255,0.12)",
      color: stateText(theme, "#C08BFF", "purple"),
      "& .MuiSvgIcon-root": { fontSize: 17 },
    },
    name: { fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: theme.palette.text.primary },
    sub: { fontSize: 10.5, color: theme.palette.aeros.textTertiary },
    chips: { display: "flex", gap: 5, flexWrap: "wrap" },
    phraseChip: {
      fontSize: 11,
      fontWeight: 600,
      borderRadius: 6,
      padding: "3px 8px",
      display: "flex",
      alignItems: "center",
      gap: 4,
      whiteSpace: "nowrap",
      "& small": { fontSize: 9, opacity: 0.7, fontWeight: 800 },
    },
    chipExact: { color: stateText(theme, "#C08BFF", "purple"), background: "rgba(160,68,255,0.12)" },
    chipPartial: { color: stateText(theme, AEROS.cyan, "info"), background: "rgba(34,211,238,0.1)" },
    flowCell: { display: "flex", alignItems: "center", gap: 6, color: theme.palette.text.secondary, fontSize: 12.5, minWidth: 0 },
    flowIcon: { fontSize: 16, color: stateText(theme, AEROS.cyan, "info"), flex: "none" },
    ellipsis: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    connChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      fontSize: 11,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
      borderRadius: 6,
      padding: "2px 8px",
      whiteSpace: "nowrap",
    },
    dot: { width: 6, height: 6, borderRadius: "50%", flex: "none" },
    hits: { fontSize: 12.5, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: theme.palette.aeros.textTertiary },
    statusPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 11,
      fontWeight: 600,
      borderRadius: 99,
      padding: "3px 10px",
      lineHeight: 1.4,
      "& .MuiSvgIcon-root": { fontSize: 13 },
    },
    pillActive: {
      color: stateText(theme, AEROS.success, "success"),
      backgroundColor: alpha(AEROS.success, 0.1),
      "& $dot": { background: stateText(theme, AEROS.success, "success") },
    },
    pillInactive: {
      color: theme.palette.text.secondary,
      backgroundColor: isLight ? "rgba(15,20,40,0.06)" : "rgba(255,255,255,0.06)",
      "& $dot": { background: theme.palette.text.secondary },
    },
    actionButton: {
      width: 30,
      height: 30,
      borderRadius: 8,
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.08)"}`,
      color: theme.palette.text.secondary,
      padding: 0,
      "&:hover": {
        color: isLight ? AEROS.stateTextOnLight.info : AEROS.cyan,
        borderColor: alpha(AEROS.cyan, 0.4),
        backgroundColor: "transparent",
      },
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    deleteButton: {
      "&:hover": {
        color: isLight ? AEROS.stateTextOnLight.error : AEROS.error,
        borderColor: alpha(AEROS.error, 0.4),
        backgroundColor: "transparent",
      },
    },
    emptyState: {
      textAlign: "center",
      padding: theme.spacing(6),
      backgroundColor: theme.palette.aeros.surface,
      borderRadius: 14,
      border: `1px solid ${border}`,
    },
    emptyIcon: { fontSize: 56, color: theme.palette.text.disabled, marginBottom: theme.spacing(2) },
    emptyTitle: { fontSize: 18, fontWeight: 600, color: theme.palette.text.primary, marginBottom: theme.spacing(1), fontFamily: AEROS.fontDisplay },
    emptyDescription: { color: theme.palette.text.secondary, fontSize: 13 },
    loading: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 },
    // ---- Fluxo padrão ----
    sectionHead: {
      height: 56,
      display: "flex",
      alignItems: "center",
      padding: "0 24px",
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.06)"}`,
      gap: 12,
    },
    sectionTitle: { fontFamily: AEROS.fontDisplay, fontSize: 15, fontWeight: 700, letterSpacing: "-0.02em", color: theme.palette.text.primary },
    sectionSub: { fontSize: 11.5, color: theme.palette.text.secondary },
    defaultRow: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 12,
      padding: "12px 14px",
      [theme.breakpoints.down("xs")]: { flexWrap: "wrap" },
    },
    defaultTile: {
      width: 34,
      height: 34,
      borderRadius: 9,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      "& .MuiSvgIcon-root": { fontSize: 18 },
    },
    defaultTitle: { fontSize: 12.5, fontWeight: 700, color: theme.palette.text.primary },
    defaultDesc: { fontSize: 11, color: theme.palette.text.secondary, lineHeight: 1.45, marginTop: 2 },
    defaultSelect: {
      width: 200,
      flex: "none",
      "& .MuiOutlinedInput-root": { borderRadius: 9, backgroundColor: theme.palette.aeros.sidebar, fontSize: 12 },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)" },
      "& .MuiSelect-root": { padding: "8px 30px 8px 10px", display: "flex", alignItems: "center", gap: 6 },
      "& .MuiSelect-icon": { color: theme.palette.aeros.textTertiary, right: 6, fontSize: 18 },
      [theme.breakpoints.down("xs")]: { width: "100%" },
    },
    muted: { color: theme.palette.aeros.textTertiary },
  };
});

export default useFlowPageStyles;
